import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { computed, ref, toRefs } from 'vue';

import { fromLonLat } from 'ol/proj';
import { useRoute, useRouter } from 'vue-router';
import { Extent } from 'ol/extent';
import ElementType from '@/modules/map/contracts/ElemetType';
import { Locality } from '@/components/Search/types/Locality';
import FeatureRoot from '@/modules/map/features/FeatureRoot';
import Map from '@/modules/map/data/Map';
import routeNames from '@/router/routeNames';
import { useDistrictStore, useMapStore, useOrganizationStore } from '@/stores';
import openStreetMapSearchApiService from '@/api/openStreetMapSearchApiService';
import { useI18n } from 'vue-i18n';

interface SelectedObject {
  type: ElementType,
  value: number,
  text: string,
  boundingbox?: number[],
  specieTitle?: string,
  varietyTitle?: string
}

type ObjectList = {
  type: string, value: number, text: string,
  specieTitle?: string, varietyTitle?: string
} | {group: string, disable: boolean, value: string};


export default /*#__PURE__*/_defineComponent({
  __name: 'Search',
  props: {
    dark: { type: Boolean, default: true }
  },
  setup(__props: any) {




const mapStore = useMapStore();
const districtStore = useDistrictStore();
const organizationStore = useOrganizationStore();
const { t } = useI18n();

const $router = useRouter();
const $route = useRoute();

const { meteos, fields, map } = toRefs(mapStore);
const { districts } = toRefs(districtStore);
const { organizations } = toRefs(organizationStore);

let localities = [];

const searchObject = ref(null);

const isLoading = ref(false);

const options = ref([]);

const getLocalitiesList = (localities: Locality[]) => localities.map((object: Locality) => ({
  type: object.type,
  value: object.place_id,
  text: object.display_name,
  boundingbox: object.boundingbox,
}));

const getObjectList = (panelObjects: FeatureRoot[]):ObjectList[] => (panelObjects
  ? panelObjects.map((object: FeatureRoot) => ({
    type: object.type,
    value: object.props.id,
    text: object.type === ElementType.Field ? `${t('fields.field')} ${object.props.num} (${object.props.title})` : object.props.title,
    ...(object.type === ElementType.Field ? {
      specieTitle: object.props.specieTitle,
      varietyTitle: object.props.varietyTitle,
    } : {}),
  })) : []);

const setSearch = (search) => {
  searchObject.value = search;
};

const clearSearch = () => {
  searchObject.value = null;
};

const panelObjects = computed<ObjectList[]>(() => {
  const allFields = getObjectList(fields.value);
  const allMeteos = getObjectList(meteos.value);
  const allDistricts = getObjectList(districts?.value);
  const allOrganizations = getObjectList(organizations?.value);

  const fieldsList = allFields.length ? [{ group: t('fields.title'), disable: true, value: 'field' }, ...allFields] : [];
  const meteoList = allMeteos.length ? [{ group: t('meteoStations.title'), disable: true, value: 'meteo' }, ...allMeteos] : [];
  const districtsList = allDistricts.length ? [{ group: t('areas.title'), disable: true, value: 'district' }, ...allDistricts] : [];
  const organizationsList = allOrganizations.length
    ? [{ group: t('organizations.title'), disable: true, value: 'organization' }, ...allOrganizations] : [];

  return [...fieldsList,
    ...meteoList,
    ...districtsList,
    ...organizationsList,
  ];
});

const filterFn = (val, update) => {
  if (val === '') {
    update(() => {
      options.value = panelObjects.value;
    });

    return;
  }

  const searchText = val.trim().toLowerCase();
  const isFound = (text: string) => text && text.toLowerCase().indexOf(searchText) > -1;
  openStreetMapSearchApiService(searchText)
    .then((response: any) => {
      const newLocalities = getLocalitiesList(response.data);
      localities = newLocalities.length
        ? [{ group: t('areas.localities'), disable: true, value: 'locality' },
          ...newLocalities] : [];
    }).catch(() => null)
    .finally(() => {
      update(() => {
        const baseOptions = panelObjects.value
        // @ts-ignore
          .filter((item) => item.group || isFound(item.text)
              // @ts-ignore
              || isFound(item.specieTitle) || isFound(item.varietyTitle));
        options.value = [...baseOptions, ...localities];
      });
    });
};

const objectSelected = (object: SelectedObject) => {
  if (object) {
    setTimeout(() => {
      searchObject.value = '';
    }, 20);
    switch (object.type) {
      case ElementType.Field:
        $router.push({
          name: routeNames.fieldsId,
          params: { id: object?.value.toString() },
        });
        break;

      case ElementType.Meteo:
        $router.push({
          name: routeNames.meteosId,
          params: { id: object?.value.toString() },
        });
        break;

      case ElementType.District:
        $router.push({
          name: routeNames.organizations,
          query: {
            districtId: object?.value.toString(),
          },
        });
        break;

      case ElementType.Organization:
        $router.push({
          name: routeNames.fields,
          query: {
            ...$route.query,
            organizationId: object?.value.toString(),
          },
        });
        break;
      default: {
        const coordsMin = fromLonLat([object.boundingbox[2], object.boundingbox[0]]);
        const coordsMax = fromLonLat([object.boundingbox[3], object.boundingbox[1]]);
        const extentCoords = [coordsMin[0], coordsMin[1], coordsMax[0], coordsMax[1]];
        (map.value as Map).getView().fit(
extentCoords as Extent,
{ duration: 1000, padding: [15, 15, 15, 15] },
        );
      }
    }
  }
  return null;
};

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_item_label = _resolveComponent("q-item-label")!
  const _component_q_item_section = _resolveComponent("q-item-section")!
  const _component_q_item = _resolveComponent("q-item")!
  const _component_q_select = _resolveComponent("q-select")!

  return (_openBlock(), _createBlock(_component_q_select, {
    "model-value": searchObject.value,
    outlined: "",
    "use-input": "",
    label: _unref(t)('search') + '...',
    options: options.value,
    loading: isLoading.value,
    "popup-content-style": "width: 300px",
    "option-label": "text",
    "option-value": "value",
    dense: "",
    "fill-input": "",
    "hide-selected": "",
    dark: _ctx.dark,
    filled: "",
    clearable: "",
    onFilter: filterFn,
    onInputValue: setSearch,
    onClear: clearSearch,
    "onUpdate:modelValue": objectSelected
  }, {
    prepend: _withCtx(() => [
      _createVNode(_component_q_icon, { name: "mdi-magnify" })
    ]),
    option: _withCtx((data) => [
      (!data.opt.group)
        ? (_openBlock(), _createBlock(_component_q_item, _mergeProps({ key: 0 }, data.itemProps, {
            key: data.opt.value
          }), {
            default: _withCtx(() => [
              _createVNode(_component_q_item_section, null, {
                default: _withCtx(() => [
                  _createVNode(_component_q_item_label, null, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(data.opt.text), 1)
                    ]),
                    _: 2
                  }, 1024),
                  _createVNode(_component_q_item_label, { caption: "" }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(data.opt.specieTitle), 1)
                    ]),
                    _: 2
                  }, 1024)
                ]),
                _: 2
              }, 1024)
            ]),
            _: 2
          }, 1040))
        : _createCommentVNode("", true),
      (data.opt.group)
        ? (_openBlock(), _createBlock(_component_q_item, _mergeProps({ key: 1 }, data.itemProps, {
            key: data.opt.value
          }), {
            default: _withCtx(() => [
              _createVNode(_component_q_item_label, { header: "" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(data.opt.group), 1)
                ]),
                _: 2
              }, 1024)
            ]),
            _: 2
          }, 1040))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["model-value", "label", "options", "loading", "dark"]))
}
}

})